import AddCircleIcon from '@mui/icons-material/AddCircle';
import {Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import {useQuery} from '@tanstack/react-query';
import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import CustomAlert from '../../components/CustomAlert/CustomAlert';
import CustomButton from '../../components/CustomButton/CustomButton';
import CustomButtonGroup from '../../components/CustomButtonGroup/CustomButtonGroup';
import CustomDrawer from '../../components/CustomDrawer/CustomDrawer';
import CustomSelectField from '../../components/CustomFormFields/CustomSelectField/CustomSelectField';
import CustomTextField from '../../components/CustomFormFields/CustomTextField/CustomTextField';
import CustomSwitch from '../../components/CustomSwitch/CustomSwitch';
import {ConfigConstant} from '../../constants/config';
import {
  fetchAncillaryData,
  insertOutletAncillaryData,
  storeOutlet,
  updateOutlet,
  updateOutletAncillaryData,
} from '../../service/Outlet';
import {getVirtualStoreDetails, getFailureReason, getChannelDetails} from '../../utils/outlet';

import UpdateOutletConfirmationDrawer from './UpdateOutletConfirmationDrawer';

const OutletDrawer = ({open, onToggleOff, selectedRow, setSnackBarAlert, dataQuery}) => {
  const formMode = selectedRow?.row ? 'UPDATE' : 'CREATE';
  const ancillaryColumns = ['stationCode', 'cutOffTimeMin', 'allowedPaymentMode', 'latitude', 'longitude', 'isActive'];
  const drawerRef = useRef();
  const {email} = useSelector((state) => state.auth);
  const commonFilters = useSelector((store) => store?.commonFilters);
  const [showFetchBtn, setShowFetchBtn] = useState(false);
  const permissions = useSelector((state) => state.permissions);
  const {editOutlet: haveUpdateOutletPermission, createOutlet: haveCreateOutletPermission} = permissions;

  const [formData, setFormData] = useState({
    partnerStoreCode: {
      type: 'text',
      label: 'Virtual Store Code',
      value: selectedRow?.row?.partnerStoreCode || '',
      hide: false,
      disabled: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
      error: false,
    },
    channel: {
      type: 'select',
      label: 'Channel',
      value:
        selectedRow?.row?.channelGroupCode && selectedRow?.row?.channelCode
          ? `${selectedRow?.row?.channelGroupCode}-${selectedRow?.row?.channelCode}`
          : '',
      options: commonFilters?.channelCode,
      hide: false,
      disabled: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
      error: false,
    },
    mappingStatus: {
      type: 'select',
      label: 'Mapping Status',
      value: selectedRow?.row?.mappingStatus || '',
      hide: formMode === 'UPDATE' ? false : true,
      options: commonFilters?.mappingStatus,
      disabled: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
      error: false,
    },
    partnerChannelCode: {
      type: 'text',
      label: 'Partner Channel Code',
      value: selectedRow?.row?.partnerChannelCode || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    channelStoreCode: {
      type: 'text',
      label: 'Channel Store Code',
      value: selectedRow?.row?.channelStoreCode || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
      error: false,
    },
    channelBrandAuthKey: {
      type: 'text',
      label: 'Channel Brand Auth Key',
      value: selectedRow?.row?.channelBrandAuthKey || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
      error: false,
    },
    partnerBrandCode: {
      type: 'text',
      label: 'Brand Code',
      value: selectedRow?.row?.partnerBrandCode || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    partnerBrandName: {
      type: 'text',
      label: 'Brand',
      value: selectedRow?.row?.partnerBrandName || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    partnerPhyStoreCode: {
      type: 'text',
      label: 'Store Code',
      value: selectedRow?.row?.partnerPhyStoreCode || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    partnerPhyStoreName: {
      type: 'text',
      label: 'Store',
      value: selectedRow?.row?.partnerPhyStoreName || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    city: {
      type: 'text',
      label: 'City',
      value: selectedRow?.row?.city || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    countryCode: {
      type: 'text',
      label: 'Country Code',
      value: selectedRow?.row?.countryCode || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
    countryName: {
      type: 'text',
      label: 'Country',
      value: selectedRow?.row?.countryName || '',
      hide: formMode === 'UPDATE' ? false : true,
      disabled: true,
      error: false,
    },
  });
  const [formAncillaryData, setFormAncillaryData] = useState([]);
  const [isAddingAncillary, setIsAddingAncillary] = useState(false);
  const [prevStoreDetails, setPrevStoreDetails] = useState({
    partnerStoreCode: selectedRow?.row?.partnerStoreCode || '',
    channel:
      selectedRow?.row?.channelGroupCode && selectedRow?.row?.channelCode
        ? `${selectedRow?.row?.channelGroupCode}-${selectedRow?.row?.channelCode}`
        : '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [alertData, setAlertData] = useState({isOpen: false, text: '', severity: 'info'});
  const [openUpdateConfirmDrawer, setOpenUpdateConfirmDrawer] = useState(false);
  const [dataToUpdate, setDataToUpdate] = useState({});
  const [ancillaryDataToUpdate, setAncillaryDataToUpdate] = useState({});
  const [ancillaryDataToInsert, setAncillaryDataToInsert] = useState({});
  const [showAddAncillaryRowBtn, setShowAddAncillaryRowBtn] = useState(false);
  const [storeData, setStoreDataState] = useState(null);
  const [cmsFieldErrors, setCmsFieldErrors] = useState([]);

  // NOTE: Move to FRONTEND_CONFIG if adding more store creation channels via Moss.
  const CHANNELS_WITH_CMS_VALIDATION = {
    ODCH00014: ['fssai_number', 'fssai_valid_upto', 'email', 'phone_number'],
  };

  const CMS_FIELD_LABELS = {
    fssai_number: 'FSSAI Number',
    fssai_valid_upto: 'FSSAI Valid Upto',
    email: 'Contact Email ID',
    phone_number: 'Contact Phone Number',
  };

  const getCmsValidationConfig = () => {
    const [channelGroupCode] = formData.channel.value?.split('-') || [];
    return CHANNELS_WITH_CMS_VALIDATION?.[channelGroupCode] || null;
  };

  const isCmsValidationChannel = () => {
    return getCmsValidationConfig() !== null;
  };

  const isAncillaryDataRequired = ConfigConstant.MONGO_CONFIG?.CHANNEL_REQUIRED_ANCILLARY_DATA.includes(
    selectedRow?.row?.channelGroupCode
  );

  const dataQueryAncillary = useQuery({
    queryKey: ['data-outlet-ancillary'],
    queryFn: () => fetchAncillaryData(selectedRow?.row?.partnerStoreCode),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: isAncillaryDataRequired,
    cacheTime: 0,
  });

  useEffect(() => {
    if (Array.isArray(dataQueryAncillary?.data?.rows)) {
      const ancillaryData = [];
      dataQueryAncillary.data.rows.forEach((data, index) => {
        ancillaryData.push({
          dbId: data?.id,
          partnerStoreCode: {
            type: 'text',
            label: 'Virtual Store Code',
            value: data?.partnerStoreCode,
            id: `${index.length + 1}-partnerStoreCode`,
          },
          channelCode: {
            type: 'text',
            label: 'channelCode',
            value: data?.channelCode,
            id: `${index.length + 1}-channelCode`,
          },
          stationCode: {
            label: 'Station Code',
            type: 'text',
            value: data?.stationCode,
            id: `${index + 1}-stationCode`,
          },
          cutOffTimeMin: {
            label: 'Cut Off Time Min',
            type: 'number',
            value: data?.cutOffTimeMin,
            id: `${index + 1}-cutOffTimeMin`,
          },
          allowedPaymentMode: {
            label: 'Allowed Payment Mode',
            type: 'text',
            value: data?.allowedPaymentMode,
            id: `${index + 1}-allowedPaymentMode`,
          },
          latitude: {
            label: 'Latitude',
            type: 'number',
            value: data?.latitude,
            id: `${index + 1}-latitude`,
          },
          longitude: {
            label: 'Longitude',
            type: 'number',
            value: data?.longitude,
            id: `${index + 1}-longitude`,
          },
          isActive: {
            label: 'Active',
            type: 'switch',
            value: data?.isActive === 'active',
            id: `${index + 1}-isActive`,
          },
        });
      });
      setFormAncillaryData(ancillaryData);
      setIsAddingAncillary(false);
    }
  }, [dataQueryAncillary?.data?.rows]);

  useEffect(() => {
    if (formMode === 'UPDATE' && CHANNELS_WITH_CMS_VALIDATION?.[selectedRow?.row?.channelGroupCode]) {
      const fetchStoreDetails = async () => {
        setIsLoading(true);
        const channelSelected = commonFilters.channelCode?.find((channel) => channel.code === formData.channel.value);
        const channelDetails = getChannelDetails(channelSelected?.label, commonFilters);
        const response = await getVirtualStoreDetails(formData.partnerStoreCode.value, channelDetails?.channelName);
        if (response?.error) {
          setAlertData({
            isOpen: true,
            text: `Error: ${response?.message || 'Failed to fetch store details'}`,
            severity: 'error',
          });
        } else {
          setStoreDetails(response);
        }
        setIsLoading(false);
      };
      fetchStoreDetails();
    }
  }, []);

  useEffect(() => {
    formMode === 'CREATE' && setShowFetchBtn(true);
  }, []);

  useEffect(() => {
    const timeId = setTimeout(() => {
      alertData.isOpen && closeAlert();
    }, 5000);
    return () => {
      clearTimeout(timeId);
    };
  }, [alertData.isOpen]);

  useEffect(() => {
    if (formData.channel.value) {
      const [channelGroupCode] = formData.channel.value?.split('-') || [];
      if (!CHANNELS_WITH_CMS_VALIDATION?.[channelGroupCode]) {
        setCmsFieldErrors([]);
      }
      if (ConfigConstant.MONGO_CONFIG?.CHANNEL_REQUIRED_ANCILLARY_DATA.includes(channelGroupCode)) {
        formMode === 'CREATE' && !showFetchBtn
          ? setShowAddAncillaryRowBtn(true)
          : formMode === 'UPDATE'
            ? setShowAddAncillaryRowBtn(true)
            : setShowAddAncillaryRowBtn(false);
      } else {
        setShowAddAncillaryRowBtn(false);
      }
    }
  }, [formData.channel.value, showFetchBtn]);

  const toggleActionButtons = (key, value) => {
    if (key === 'partnerStoreCode') {
      if (prevStoreDetails.partnerStoreCode === value && prevStoreDetails.channel === formData.channel.value) {
        showFetchBtn && setShowFetchBtn(false);
      } else {
        !showFetchBtn && setShowFetchBtn(true);
      }
    } else if (key === 'channel') {
      if (prevStoreDetails.partnerStoreCode === formData.partnerStoreCode.value && prevStoreDetails.channel === value) {
        showFetchBtn && setShowFetchBtn(false);
      } else {
        !showFetchBtn && setShowFetchBtn(true);
      }
    }
  };

  const handleOnChangeFormFields = (e) => {
    const key = e.target.id;
    toggleActionButtons(key, e.target.value);
    setFormData((prevFormData) => {
      const newState = {
        ...prevFormData,
        [key]: {
          ...prevFormData[key],
          value: e.target?.value?.trim(),
          error: false,
          helperText: '',
        },
      };
      if (key !== 'channelBrandAuthKey') {
        newState.channelBrandAuthKey = {
          ...prevFormData.channelBrandAuthKey,
          error: false,
          helperText: '',
        };
      }
      return newState;
    });
    if (key === 'partnerStoreCode' || key === 'channel') {
      let value = e.target?.value?.trim();
      if (key === 'channel') {
        const [channelGroupCode] = (value || '').split('-');
        value = channelGroupCode;
      }
      setFormAncillaryData((prevState) => {
        const newState = [...prevState];
        newState.forEach((data) => {
          data[key] = value;
        });
        return newState;
      });
    }
  };

  const validateFormData = (validateFor) => {
    const formErrors = [];
    const ancillaryFormErrors = [];
    Object.keys(formData).forEach((key) => {
      if (key === 'partnerStoreCode') {
        if (!formData[key].value) formErrors.push({id: key, message: 'Required'});
        else if (formData[key].value?.length !== 12)
          formErrors.push({id: key, message: 'Length must be 12 characters'});
        else if (!formData[key].value?.startsWith('VST'))
          formErrors.push({id: key, message: 'Must be in format VSTXXXXXXXXX'});
      }
      if (key === 'channel' && !formData[key].value) formErrors.push({id: key, message: 'Required'});
      if (
        validateFor === 'save' &&
        formMode === 'CREATE' && //NOTE: avoiding restriction in update mode to support legacy stores
        isCmsValidationChannel() &&
        key === 'channelStoreCode' &&
        formData[key].value &&
        formData.partnerStoreCode.value &&
        formData[key].value !== formData.partnerStoreCode.value
      ) {
        formErrors.push({id: key, message: 'Channel Store Code should be same as Virtual Store Code.'});
      }
      if (validateFor === 'save' && key === 'channelBrandAuthKey') {
        const selectedChannel = commonFilters?.channelCode?.find((channel) => channel.code === formData.channel.value);
        if (
          ConfigConstant.MONGO_CONFIG?.BRAND_AUTH_KEY_REQUIRED_FOR_CHANNELS?.includes(selectedChannel?.label) &&
          !formData[key].value
        ) {
          formErrors.push({id: key, message: 'Required'});
        }
      } else if (validateFor === 'save' && !formData[key].value) formErrors.push({id: key, message: 'Required'});
    });
    if (showAddAncillaryRowBtn) {
      formAncillaryData.forEach((data) => {
        const isValidRow =
          data.dbId ||
          data.stationCode.value ||
          data.cutOffTimeMin.value ||
          data.allowedPaymentMode.value ||
          data.latitude.value ||
          data.longitude.value;
        if (isValidRow) {
          ancillaryColumns.forEach((key) => {
            if (key !== 'isActive') {
              if (!data[key].value) {
                ancillaryFormErrors.push({id: data[key]?.id, message: 'Required'});
              } else if (key === 'cutOffTimeMin' && data.cutOffTimeMin.value < 60) {
                ancillaryFormErrors.push({id: data[key]?.id, message: 'Must be equal or more than 60'});
              }
            }
          });
        }
      });
    }
    const cmsErrors = [];
    const hasNewAncillaryRows = formAncillaryData.some((data) => !data.dbId && data.stationCode?.value);
    if (validateFor === 'save' && isCmsValidationChannel() && hasNewAncillaryRows) {
      const requiredFields = getCmsValidationConfig();
      const storeDetailsData = storeData?.storeDetails || {};
      requiredFields.forEach((field) => {
        if (!storeDetailsData[field]) {
          cmsErrors.push({
            field,
            message: `${CMS_FIELD_LABELS[field] || field} is a mandatory field. Please update this in CMS.`,
          });
        } else if (field === 'fssai_number' && storeDetailsData[field].length !== 14) {
          cmsErrors.push({
            field,
            message: `${CMS_FIELD_LABELS[field] || field} must be exactly 14 characters. Please update this in CMS.`,
          });
        }
      });
    }
    const isValid = formErrors.length === 0 && ancillaryFormErrors.length === 0 && cmsErrors.length === 0;
    return {isValid, formErrors, ancillaryFormErrors, cmsErrors};
  };

  const setFormErrors = ({formErrors, ancillaryFormErrors}) => {
    if (Array.isArray(formErrors) && formErrors.length > 0) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          const data = prevState[key];
          const error = formErrors.find((err) => err.id === key);
          if (error) {
            updatedFormData[key] = {
              ...data,
              error: true,
              helperText: error.message || '',
            };
          } else {
            updatedFormData[key] = data;
          }
        });
        return updatedFormData;
      });
    }
    if (Array.isArray(ancillaryFormErrors) && ancillaryFormErrors.length > 0) {
      setFormAncillaryData((prevState) => {
        const newState = [...prevState];
        ancillaryFormErrors.forEach((err) => {
          const [index, key] = err?.id?.split('-') || [];
          if (index > 0 && key) {
            newState[index - 1][key].error = true;
            newState[index - 1][key].helperText = err?.message;
          }
        });
        return newState;
      });
    }
  };

  const setStoreDetails = (details) => {
    setStoreDataState(details);
    setPrevStoreDetails({
      partnerStoreCode: formData.partnerStoreCode.value,
      channel: formData.channel.value,
    });
    setFormData((prevState) => {
      return {
        ...prevState,
        mappingStatus: {
          ...prevState.mappingStatus,
          value: 'active',
          hide: false,
        },
        channelStoreCode: {
          ...prevState.channelStoreCode,
          hide: false,
        },
        channelBrandAuthKey: {
          ...prevState.channelBrandAuthKey,
          hide: false,
        },
        partnerChannelCode: {
          ...prevState.partnerChannelCode,
          hide: false,
          value: details?.partnerChannelCode || '',
        },
        partnerBrandCode: {
          ...prevState.partnerBrandCode,
          hide: false,
          value: details?.partnerBrandCode || '',
        },
        partnerBrandName: {
          ...prevState.partnerBrandName,
          hide: false,
          value: details?.partnerBrandName || '',
        },
        partnerPhyStoreCode: {
          ...prevState.partnerPhyStoreCode,
          hide: false,
          value: details?.partnerPhyStoreCode || '',
        },
        partnerPhyStoreName: {
          ...prevState.partnerPhyStoreName,
          hide: false,
          value: details?.partnerPhyStoreName || '',
        },
        city: {
          ...prevState.city,
          hide: false,
          value: details?.city || '',
        },
        countryCode: {
          ...prevState.countryCode,
          hide: false,
          value: details?.countryCode || '',
        },
        countryName: {
          ...prevState.countryName,
          hide: false,
          value: details?.countryName || '',
        },
      };
    });
    setFormAncillaryData((prevState) => {
      const newState = [...prevState];
      const [channelGroupCode] = formData?.channel?.value?.split('-') || [];
      newState.forEach((data) => {
        data.partnerStoreCode = formData?.partnerStoreCode?.value;
        data.channelCode = channelGroupCode;
      });
      return newState;
    });
    setShowFetchBtn(false);
  };

  const handleOnFetchDetails = async () => {
    const {isValid, formErrors} = validateFormData('fetch');
    if (!isValid) {
      return setFormErrors({formErrors});
    }
    setIsLoading(true);
    const channelSelected = commonFilters.channelCode?.find((channel) => channel.code === formData.channel.value);
    const channelDetails = getChannelDetails(channelSelected?.label, commonFilters);
    const response = await getVirtualStoreDetails(formData.partnerStoreCode.value, channelDetails?.channelName);
    if (response?.error) {
      setAlertData({
        isOpen: true,
        text: `Error: ${response?.message || 'Failed to fetch store details'}`,
        severity: 'error',
      });
    } else {
      setStoreDetails(response);
    }
    setIsLoading(false);
  };

  const createDataForSave = () => {
    const dataToSave = {
      createdBy: email,
      updatedBy: email,
      partnerCode: ConfigConstant.CODE_REBELPLUS,
      channelStoreGroupCode: formData.channelStoreCode.value,
    };
    Object.keys(formData).forEach((key) => {
      if (key === 'channel') {
        const [channelGroupCode, channelCode] = formData[key]?.value?.split('-') || [];
        dataToSave.channelCode = channelCode;
        dataToSave.channelGroupCode = channelGroupCode;
      } else {
        dataToSave[key] = formData[key]?.value;
      }
    });
    const ancillaryDataToSave = [];
    if (showAddAncillaryRowBtn) {
      formAncillaryData.forEach((data) => {
        const isValidRow =
          data.stationCode.value &&
          data.cutOffTimeMin.value &&
          data.allowedPaymentMode.value &&
          data.latitude.value &&
          data.longitude.value;
        if (isValidRow) {
          ancillaryDataToSave.push({
            createdBy: email,
            updatedBy: email,
            partnerCode: ConfigConstant.CODE_REBELPLUS,
            channelCode: data?.channelCode?.value,
            partnerStoreCode: data?.partnerStoreCode?.value,
            stationCode: data.stationCode.value,
            cutOffTimeMin: Number(data.cutOffTimeMin.value),
            allowedPaymentMode: data.allowedPaymentMode.value,
            latitude: data.latitude.value,
            longitude: data.longitude.value,
            isActive: data.isActive.value ? 'active' : 'inactive',
          });
        }
      });
    }
    return {dataToSave, ancillaryDataToSave};
  };

  const createDataForUpdate = async () => {
    const dataToUpdate = {};
    const ancillaryDataToUpdate = [];
    const ancillaryDataToInsert = [];
    Object.keys(formData).forEach((key) => {
      if (key === 'channel') {
        const selectedChannel = commonFilters?.channelCode?.find((channel) => channel.code === formData.channel.value);
        const existingValue =
          selectedRow?.row?.channelGroupName === selectedRow?.row?.channelName
            ? `${selectedRow?.row?.channelName}-direct`
            : `${selectedRow?.row?.channelGroupName}-${selectedRow?.row?.channelName}`;
        if (existingValue !== selectedChannel?.label) {
          dataToUpdate.channel = selectedChannel?.label;
        }
      } else {
        const existingValue = selectedRow?.row?.[key];
        const newValue = formData[key]?.value;
        if (!(existingValue === null && newValue === '')) {
          if (existingValue !== newValue) {
            dataToUpdate[key] = newValue;
          }
        }
      }
    });
    formAncillaryData.forEach((data, index) => {
      const existingValue = (dataQueryAncillary?.data?.rows || []).find((d) => d?.id === data.dbId);
      const isValidRow =
        data.stationCode.value &&
        data.cutOffTimeMin.value &&
        data.allowedPaymentMode.value &&
        data.latitude.value &&
        data.longitude.value;
      if (isValidRow) {
        const tempData = {};
        ancillaryColumns.forEach((key) => {
          const existingValueForIsActive = existingValue?.['isActive'] === 'active' ? true : false;
          if (key === 'isActive') {
            if (data[key]?.value !== existingValueForIsActive)
              tempData[key] = data[key]?.value === true ? 'active' : 'inactive';
          } else if (key === 'cutOffTimeMin') {
            if (data[key]?.value !== existingValue?.[key]) tempData[key] = Number(data[key]?.value);
          } else if (data[key]?.value !== existingValue?.[key]) {
            tempData[key] = data[key]?.value;
          }
        });
        if (dataToUpdate?.channel) {
          const [, channelCode] = formData?.channel?.value?.split('-') || [];
          tempData.channelCode = channelCode;
        }
        if (dataToUpdate?.partnerStoreCode?.value) {
          tempData.channelCode = dataToUpdate?.partnerStoreCode;
        }
        if (Object.keys(tempData).length > 0) {
          tempData.index = index;
          if (data.dbId) {
            tempData.id = data.dbId;
            ancillaryDataToUpdate.push(tempData);
          } else {
            const [channelGroupCode] = formData?.channel?.value?.split('-') || [];
            tempData.channelCode = channelGroupCode; // keeping identical to handleAddAncillaryRow:918
            tempData.partnerStoreCode = formData?.partnerStoreCode?.value;
            ancillaryDataToInsert.push({...tempData});
          }
        }
      }
    });
    if (!(dataToUpdate?.channel || dataToUpdate?.partnerStoreCode)) {
      const channelSelected = commonFilters.channelCode?.find((channel) => channel.code === formData.channel.value);
      const channelDetails = getChannelDetails(channelSelected?.label, commonFilters);
      const response = await getVirtualStoreDetails(formData.partnerStoreCode.value, channelDetails?.channelName);
      if (!response?.error) {
        Object.keys(response).forEach((key) => {
          if (formData[key] && response[key] !== formData[key].value) {
            dataToUpdate[key] = response[key];
          }
        });
        setStoreDetails(response);
      }
    }
    return {dataToUpdate, ancillaryDataToUpdate, ancillaryDataToInsert};
  };

  const handleOnSave = async () => {
    if (isLoading) return;
    if (drawerRef.current) {
      drawerRef.current.scrollTop = 0;
    }
    const {isValid, formErrors, ancillaryFormErrors, cmsErrors} = validateFormData('save');
    if (!isValid) {
      setCmsFieldErrors(cmsErrors);
      setFormErrors({formErrors, ancillaryFormErrors});
      return;
    }
    setIsLoading(true);
    if (formMode === 'CREATE') {
      const {dataToSave, ancillaryDataToSave} = createDataForSave();
      const response = await storeOutlet({data: [dataToSave]});
      if (response?.status !== 200) {
        setIsLoading(false);
        return setAlertData({
          isOpen: true,
          text: `Error: ${getFailureReason(
            response.data?.data?.error || response.data?.data?.message || 'something went wrong'
          )}`,
          severity: 'error',
        });
      } else {
        if (Array.isArray(ancillaryDataToSave) && ancillaryDataToSave?.length > 0) {
          const response = await insertOutletAncillaryData({
            data: {
              ancillary: ancillaryDataToSave,
              storeDetails: storeData?.storeDetails || null,
            },
          });
          if (response?.status !== 200) {
            const errorData = response.data?.data;
            const errorMsg = Array.isArray(errorData?.error)
              ? errorData.error.join('; ')
              : errorData?.error || errorData?.message || '';
            setSnackBarAlert((prevState) => ({
              ...prevState,
              isOpen: true,
              severity: 'error',
              message: `outlet mapping saved successfully, but failed to save ancillary data: ${errorMsg}`,
            }));
          } else {
            let isPartialSuccess = false;
            const rejectionMessages = [];
            response.data?.data?.data?.forEach((item) => {
              if (item.status === 'rejected') {
                isPartialSuccess = true;
                rejectionMessages.push(item.message);
                return;
              }
            });
            if (isPartialSuccess) {
              setSnackBarAlert((prevState) => ({
                ...prevState,
                isOpen: true,
                severity: 'error',
                message: `outlet mapping saved successfully, but failed to save SOME ancillary data: ${rejectionMessages.join('; ')}`,
              }));
            } else {
              setSnackBarAlert({isOpen: true, severity: 'success', message: 'successfully saved'});
            }
          }
        } else {
          setSnackBarAlert({isOpen: true, severity: 'success', message: 'successfully saved'});
        }
        dataQuery.refetch();
      }
      setIsLoading(false);
      onToggleOff();
    } else if (formMode === 'UPDATE') {
      const {dataToUpdate, ancillaryDataToUpdate, ancillaryDataToInsert} = await createDataForUpdate();
      setIsLoading(false);
      if (
        Object.keys(dataToUpdate)?.length > 0 ||
        ancillaryDataToUpdate?.length > 0 ||
        ancillaryDataToInsert.length > 0
      ) {
        return handleDrawerOpen({dataToUpdate, ancillaryDataToUpdate, ancillaryDataToInsert});
      } else {
        return setAlertData({
          isOpen: true,
          text: `No updates made. Please make changes before clicking 'Update.'`,
          severity: 'info',
        });
      }
    }
  };

  const handleOnUpdate = async () => {
    if (isLoading) return;
    handleDrawerClose();
    setIsAddingAncillary(false);
    setIsLoading(true);
    const isOutletupdateRequired = Object.keys(dataToUpdate)?.length > 0;
    let isSuccessUpdateAncillary = true;
    let isSuccessInsertAncillary = true;
    let insertErrorMsg = '';
    let updateErrorMsg = '';
    const clearData = {
      partnerCode: ConfigConstant.CODE_REBELPLUS,
      channelName: selectedRow?.row?.channelName,
      channelGroupName: selectedRow?.row?.channelGroupName,
      partnerStoreCode: selectedRow?.row?.partnerStoreCode,
      channelStoreCode: selectedRow?.row?.channelStoreCode,
    };
    if (isOutletupdateRequired) {
      if (dataToUpdate?.channel || dataToUpdate?.partnerStoreCode) {
        const [channelGroupCode, channelCode] = formData?.channel?.value?.split('-') || [];
        if (dataToUpdate?.channel) {
          dataToUpdate.channelCode = channelCode;
          dataToUpdate.channelGroupCode = channelGroupCode;
        }
        formAncillaryData.forEach((data) => {
          if (data?.dbId) {
            ancillaryDataToUpdate.push({
              id: data?.dbId,
              ...(dataToUpdate?.channel && {channelCode: channelGroupCode}),
              ...(dataToUpdate?.partnerStoreCode && {partnerStoreCode: dataToUpdate?.partnerStoreCode}),
            });
          }
        });
      }
      const data = {
        data: {
          ...dataToUpdate,
          id: Number(selectedRow?.row?.id),
          updatedBy: email,
        },
        clearData,
      };
      if (dataToUpdate?.channelStoreCode) {
        data.data.channelStoreGroupCode = dataToUpdate?.channelStoreCode;
      }
      const response = await updateOutlet(data);
      if (response?.status !== 200) {
        setIsLoading(false);
        return setAlertData({
          isOpen: true,
          text: `Error: ${getFailureReason(
            response.data?.data?.error || response.data?.data?.message || 'something went wrong'
          )}`,
          severity: 'error',
        });
      }
      dataQuery?.refetch();
    }
    if (ancillaryDataToUpdate.length > 0) {
      ancillaryDataToUpdate.forEach((data) => {
        data.id = Number(data?.id);
        data.updatedBy = email;
      });
      const updateAncillaryResponse = await updateOutletAncillaryData({data: ancillaryDataToUpdate, clearData});
      if (updateAncillaryResponse?.status !== 200) {
        isSuccessUpdateAncillary = false;
        const errorData = updateAncillaryResponse.data?.data;
        updateErrorMsg = Array.isArray(errorData?.error)
          ? errorData.error.join('; ')
          : errorData?.error || errorData?.message || '';
      } else {
        updateAncillaryResponse.data?.data?.data?.forEach((item) => {
          if (item.status === 'rejected') {
            isSuccessUpdateAncillary = false;
            updateErrorMsg = item.message;
            return;
          }
        });
      }
    }
    if (ancillaryDataToInsert.length > 0) {
      ancillaryDataToInsert.forEach((data) => {
        data.partnerCode = ConfigConstant?.CODE_REBELPLUS;
        data.createdBy = email;
        data.updatedBy = email;
      });
      const insertAncillaryResponse = await insertOutletAncillaryData({
        data: {
          ancillary: ancillaryDataToInsert,
          storeDetails: storeData?.storeDetails || null,
        },
      });
      if (insertAncillaryResponse?.status !== 200) {
        isSuccessInsertAncillary = false;
        const errorData = insertAncillaryResponse.data?.data;
        insertErrorMsg = Array.isArray(errorData?.error)
          ? errorData.error.join('; ')
          : errorData?.error || errorData?.message || '';
      } else {
        insertAncillaryResponse.data?.data?.data?.forEach((item) => {
          if (item.status === 'rejected') {
            isSuccessInsertAncillary = false;
            insertErrorMsg = item.message;
            return;
          }
        });
      }
    }
    if (isSuccessUpdateAncillary && isSuccessInsertAncillary) {
      setSnackBarAlert({isOpen: true, severity: 'success', message: 'successfully saved'});
    } else {
      const errorMsgSuffix = [updateErrorMsg, insertErrorMsg].filter(Boolean).join('; ');
      isOutletupdateRequired
        ? setSnackBarAlert((prevState) => ({
            ...prevState,
            isOpen: true,
            severity: 'error',
            message: `outlet mapping saved successfully, but failed to save ancillary data: ${errorMsgSuffix}.`,
          }))
        : setSnackBarAlert((prevState) => ({
            ...prevState,
            isOpen: true,
            severity: 'error',
            message: `failed to save ancillary data: ${errorMsgSuffix}.`,
          }));
    }
    setIsLoading(false);
    onToggleOff();
  };

  const closeAlert = () => {
    setAlertData((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  const handleDrawerOpen = ({dataToUpdate, ancillaryDataToUpdate, ancillaryDataToInsert}) => {
    setDataToUpdate(dataToUpdate);
    setAncillaryDataToUpdate(ancillaryDataToUpdate);
    setAncillaryDataToInsert(ancillaryDataToInsert);
    setOpenUpdateConfirmDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenUpdateConfirmDrawer(false);
  };

  const handleAddAncillaryRow = () => {
    setIsAddingAncillary(true);
    const [channelGroupCode] = (formData?.channel?.value || '').split('-');
    setFormAncillaryData((prevState) => [
      ...prevState,
      {
        partnerStoreCode: {
          type: 'text',
          label: 'Virtual Store Code',
          value: formData?.partnerStoreCode?.value || '',
          id: `${prevState.length + 1}-partnerStoreCode`,
        },
        channelCode: {
          type: 'text',
          label: 'channelCode',
          value: channelGroupCode || '',
          id: `${prevState.length + 1}-channelCode`,
        },
        stationCode: {
          label: 'Station Code',
          type: 'text',
          id: `${prevState.length + 1}-stationCode`,
          value: '',
        },
        cutOffTimeMin: {
          label: 'Cut Off Time Min',
          type: 'number',
          id: `${prevState.length + 1}-cutOffTimeMin`,
          value: '',
        },
        allowedPaymentMode: {
          label: 'Allowed Payment Mode',
          type: 'text',
          id: `${prevState.length + 1}-allowedPaymentMode`,
          value: '',
        },
        latitude: {
          label: 'Latitude',
          type: 'number',
          id: `${prevState.length + 1}-latitude`,
          value: '',
        },
        longitude: {
          label: 'Longitude',
          type: 'number',
          id: `${prevState.length + 1}-longitude`,
          value: '',
        },
        isActive: {
          label: 'Active',
          type: 'switch',
          id: `${prevState.length + 1}-isActive`,
          value: true,
        },
      },
    ]);
  };

  const handleOnChangeAncillaryFormFields = (e) => {
    const [index, key] = e.target?.id?.split('-') || [];
    if (index > 0 && key) {
      setFormAncillaryData((prevState) => {
        const newState = [...prevState];
        newState[index - 1][key].value = key === 'isActive' ? e.target?.checked : e.target?.value;
        ancillaryColumns.forEach((fieldKey) => {
          newState[index - 1][fieldKey].error = false;
          newState[index - 1][fieldKey].helperText = '';
        });
        return newState;
      });
    }
  };

  return (
    <CustomDrawer open={open} onToggleOff={onToggleOff} anchor={'right'} title={'Outlet Details'} actionButtons={false}>
      {(isLoading ||
        (isAncillaryDataRequired && (dataQueryAncillary?.isLoading || dataQueryAncillary?.isFetching))) && (
        <LinearProgress />
      )}
      <CustomAlert
        isOpen={alertData.isOpen}
        closeAlert={closeAlert}
        text={alertData.text}
        severity={alertData.severity}
      />
      <Box ref={drawerRef}>
        <Box sx={{p: 1}}>
          {Object.keys(formData)?.map((key, index) => {
            if (!formData[key].hide && formData[key].type === 'text') {
              return (
                <CustomTextField
                  key={key}
                  label={formData[key].label}
                  id={key}
                  disabled={formData[key].disabled}
                  value={formData[key]?.value}
                  onChange={handleOnChangeFormFields}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                  minWidth={215}
                />
              );
            }
            if (!formData[key].hide && formData[key].type === 'select') {
              return (
                <CustomSelectField
                  key={key}
                  id={key}
                  label={formData[key].label}
                  disabled={formData[key].disabled}
                  options={formData[key].options}
                  value={formData[key]?.value}
                  onChange={handleOnChangeFormFields}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                />
              );
            }
            return null;
          })}
        </Box>
        {/* CMS Data Fields for channels requiring CMS validation, shown in UPDATE mode or after fetch in CREATE mode */}
        {isCmsValidationChannel() && (formMode === 'UPDATE' || !showFetchBtn) && (
          <Box sx={{p: 1}}>
            {getCmsValidationConfig()?.map((field) => (
              <CustomTextField
                key={field}
                label={`${CMS_FIELD_LABELS[field] || field}`}
                id={`cms${field}`}
                disabled={true}
                value={!storeData && isLoading ? 'Fetching from CMS...' : storeData?.storeDetails?.[field] || ''}
                error={cmsFieldErrors.some((e) => e.field === field)}
                helperText={cmsFieldErrors.find((e) => e.field === field)?.message || ''}
                minWidth={215}
              />
            ))}
          </Box>
        )}
        {showAddAncillaryRowBtn && Array.isArray(formAncillaryData) && formAncillaryData.length > 0 && (
          <Box sx={{p: 2}}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Station Code</TableCell>
                  <TableCell>Cut Off Time Min</TableCell>
                  <TableCell>Allowed Payment Mode</TableCell>
                  <TableCell>Latitude</TableCell>
                  <TableCell>Longitude</TableCell>
                  <TableCell>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formAncillaryData.map((data, index) => (
                  <TableRow key={index}>
                    {ancillaryColumns.map((key, i) => (
                      <TableCell key={i} sx={{p: 0}}>
                        {data[key]?.type === 'text' || data[key]?.type === 'number' ? (
                          <CustomTextField
                            id={data[key]?.id}
                            type={data[key]?.type}
                            value={data[key]?.value}
                            onChange={handleOnChangeAncillaryFormFields}
                            error={data[key]?.error}
                            helperText={data[key]?.helperText || ''}
                          />
                        ) : data[key]?.type === 'switch' && haveUpdateOutletPermission ? (
                          <CustomSwitch
                            id={data[key]?.id}
                            checked={data[key]?.value}
                            onChange={handleOnChangeAncillaryFormFields}
                          />
                        ) : null}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {showAddAncillaryRowBtn && haveUpdateOutletPermission && !isAddingAncillary && (
          <Box sx={{p: 2}}>
            <CustomButton
              text={
                <>
                  <AddCircleIcon />
                  <span>Add row</span>
                </>
              }
              onClick={handleAddAncillaryRow}
            />
          </Box>
        )}
        <Box sx={{display: 'flex', justifyContent: 'end'}}>
          <CustomButtonGroup
            first={{
              text: 'Cancel',
              color: 'primary',
              variant: 'outlined',
              onClick: onToggleOff,
              size: 'medium',
              hide: formMode === 'CREATE' ? !haveCreateOutletPermission : !haveUpdateOutletPermission,
            }}
            second={{
              text: formMode === 'CREATE' ? 'Save' : 'Update',
              color: formMode === 'CREATE' ? 'secondary' : 'primary',
              className: formMode === 'CREATE' ? 'color-secondary' : '',
              variant: 'contained',
              size: 'medium',
              hide:
                formMode === 'CREATE'
                  ? showFetchBtn || !haveCreateOutletPermission
                  : showFetchBtn || !haveUpdateOutletPermission,
              isLoading,
              onClick: handleOnSave,
            }}
            third={{
              text: 'Fetch Details',
              color: 'primary',
              variant: 'contained',
              size: 'medium',
              hide: !showFetchBtn,
              isLoading,
              onClick: handleOnFetchDetails,
            }}
          />
        </Box>
        {openUpdateConfirmDrawer && (
          <UpdateOutletConfirmationDrawer
            open={openUpdateConfirmDrawer}
            onToggleOff={handleDrawerClose}
            formData={formData}
            selectedRow={selectedRow}
            dataToUpdate={dataToUpdate}
            ancillaryColumns={ancillaryColumns}
            formAncillaryData={formAncillaryData}
            dataQueryAncillary={dataQueryAncillary}
            ancillaryDataToUpdate={ancillaryDataToUpdate}
            ancillaryDataToInsert={ancillaryDataToInsert}
            handleOnUpdate={handleOnUpdate}
          />
        )}
      </Box>
    </CustomDrawer>
  );
};

export default OutletDrawer;
